/**
 * Constants for Reflux replacement module.
 * Centralizes magic strings used across the module.
 *
 * @module reflux/constants
 */

// ============================================================================
// ACTION RESULT NAMES (Child Actions for Async Operations)
// ============================================================================

/**
 * Canonical action result name for successful completion.
 * Used as child action on async actions (e.g., login.complete).
 * @constant {string}
 */
export const ACTION_RESULT_COMPLETE = 'complete';

/**
 * Canonical action result name for failure.
 * Used as child action on async actions (e.g., login.fail).
 * @constant {string}
 */
export const ACTION_RESULT_FAIL = 'fail';

/**
 * Alias for ACTION_RESULT_COMPLETE (legacy Reflux compatibility).
 * @constant {string}
 */
export const ACTION_RESULT_COMPLETED = 'completed';

/**
 * Alias for ACTION_RESULT_FAIL (legacy Reflux compatibility).
 * @constant {string}
 */
export const ACTION_RESULT_FAILED = 'failed';

/**
 * Set of all standard action result names.
 * Used for iteration during store handler binding.
 * @constant {Set<string>}
 */
export const STANDARD_ACTION_RESULT_NAMES = new Set([
  ACTION_RESULT_COMPLETE,
  ACTION_RESULT_FAIL,
  ACTION_RESULT_COMPLETED,
  ACTION_RESULT_FAILED,
]);

/**
 * Map of alias action result names to their canonical counterparts.
 * Used to detect and skip aliases during binding to prevent double-invocation.
 *
 * Background: Reflux v0.4.1 creates BOTH 'complete' AND 'completed' properties
 * that point to the SAME action object. Without alias detection, we'd bind
 * the same action twice, causing handlers to be called twice per event.
 *
 * @constant {Map<string, string>}
 */
export const ACTION_RESULT_ALIAS_MAP = new Map([
  [ACTION_RESULT_COMPLETED, ACTION_RESULT_COMPLETE],
  [ACTION_RESULT_FAILED, ACTION_RESULT_FAIL],
]);

/**
 * Default action result names for asyncResult actions.
 * When asyncResult: true is set without explicit children, these are created.
 * @constant {string[]}
 */
export const DEFAULT_ASYNC_RESULTS = [ACTION_RESULT_COMPLETED, ACTION_RESULT_FAILED];

// ============================================================================
// ACTION PROPERTY NAMES
// ============================================================================

/**
 * Built-in properties that exist on every action object.
 * These are methods or metadata, NOT child actions.
 * @constant {Set<string>}
 */
export const ACTION_BUILT_IN_PROPERTIES = new Set([
  'trigger',
  'triggerAsync',
  'listen',
  'listenAndPromise',
  'eventLabel',
  'emitter',
  'definition',
]);

/**
 * All properties to skip when searching for custom child actions.
 * Includes both built-in properties and standard action result names
 * (which are handled separately with alias detection).
 * @constant {Set<string>}
 */
export const ACTION_INTERNAL_PROPERTIES = new Set([
  ...ACTION_BUILT_IN_PROPERTIES,
  ...STANDARD_ACTION_RESULT_NAMES,
]);

// ============================================================================
// STORE INTERNAL PROPERTIES
// ============================================================================

/**
 * Internal store properties filtered out when displaying state in DevTools.
 * Combines publisher methods, listener methods, and store-specific properties.
 * Used by DevTools to show only user-defined state properties.
 * @constant {Set<string>}
 */
export const STORE_INTERNAL_PROPS = new Set([
  // Publisher methods (from publisher.js)
  'emitter',
  'eventLabel',
  'preEmit',
  'shouldEmit',
  'listen',
  'trigger',
  'triggerAsync',
  // Listener methods (from listener.js)
  'hasListener',
  'listenTo',
  'listenToMany',
  'stopListeningTo',
  'stopListeningToAll',
  'subscriptions',
  // Store-specific
  'storeName',
  'init',
  'getInitialState',
  'listenables',
]);
